import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useVoices } from '../hooks/useVoices';
import { ttsService } from '../services/ttsService';
import { useAnalytics } from '../hooks/useAnalytics';
import voiceAnalytics from '../utils/voiceAnalytics';

const Playground = () => {
  const [text, setText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const { voices, isLoading, error } = useVoices();
  const { trackPageView, trackButtonClick } = useAnalytics();

  const handleTestVoice = async () => {
    try {
      setIsConverting(true);
      setAudioUrl(null);
      
      // Track voice generation with voice ID and user message
      const generationStartTime = Date.now();
      
      // Track voice generation button click with voice ID and message
      voiceAnalytics.trackVoiceGenerationClick(selectedVoice, text, {
        source: 'playground',
        text_length: text.length
      });
      
      // Track TTS test initiated with voice ID and message
      trackButtonClick('test_voice', 'playground', {
        voice_id: selectedVoice,
        text_length: text.length,
        user_message: text.substring(0, 100) // First 100 chars for privacy
      });
      
      voiceAnalytics.trackTTSUsage(selectedVoice, {
        text_length: text.length,
        user_message: text.substring(0, 100), // First 100 chars for privacy
        source: 'playground',
        test_mode: true
      });
      
      const audioData = await ttsService.textToSpeech(selectedVoice, text);
      const blob = new Blob([audioData], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      
      // Track successful generation with voice ID and message details
      const generationTime = Date.now() - generationStartTime;
      voiceAnalytics.trackTTSGenerated(selectedVoice, {
        text_length: text.length,
        user_message: text.substring(0, 100), // First 100 chars for privacy
        audio_size: audioData.byteLength,
        generation_time: generationTime,
        source: 'playground'
      });
      
    } catch (err) {
      alert('Error converting text to speech: ' + err.message);
      
      // Track error with voice ID and message details
      voiceAnalytics.trackTTSError(selectedVoice, err.message, {
        text_length: text.length,
        user_message: text.substring(0, 100), // First 100 chars for privacy
        source: 'playground'
      });
    } finally {
      setIsConverting(false);
    }
  };

  // Track page view on mount
  useEffect(() => {
    trackPageView('playground', {
      entry_point: 'navigation',
      total_voices_available: voices.length
    });
  }, [trackPageView, voices.length]);

  // Track voice selection
  const handleVoiceSelect = (voiceId) => {
    setSelectedVoice(voiceId);
    
    const selectedVoiceData = voices.find(v => v.id === voiceId);
    voiceAnalytics.trackVoiceSelection(voiceId, 'playground', {
      voice_name: selectedVoiceData?.name,
      voice_category: selectedVoiceData?.category
    });
  };

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-sidebar border-b border-border/60 sticky top-0 z-10">
        <div className="flex h-20 items-center justify-between px-8 max-w-7xl mx-auto">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-card-foreground tracking-tight">Playground</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-8 space-y-8">
        {/* Error Display */}
        {error && (
          <Card className="border border-destructive/50 bg-destructive/10 shadow-none">
            <CardContent className="p-4">
              <p className="text-sm text-destructive font-medium">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Main Card - Centered */}
        <Card className="border border-border bg-card shadow-none">
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Voice Selection */}
              <div>
                <div className="font-semibold text-card-foreground mb-1">Voice</div>
                <div className="text-muted-foreground text-sm mb-4">Select the 44agents voice you want to use.</div>
                {isLoading ? (
                  <Skeleton className="h-11 w-full" />
                ) : (
                  <Select
                    value={selectedVoice}
                    onValueChange={(value) => {
                      handleVoiceSelect(value);
                    }}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={isLoading ? "Loading voices..." : "Select a voice"} />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          {voice.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Text Input */}
              <div>
                <div className="font-semibold text-card-foreground mb-1">Text Input</div>
                <div className="text-muted-foreground text-sm mb-4">Enter the text you want to convert to speech.</div>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a tagline for an ice cream shop"
                  className="min-h-[300px] resize-none"
                />
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">{text.length} characters</span>
                  <Button
                    onClick={handleTestVoice}
                    disabled={!text || !selectedVoice || isConverting}
                    size="lg"
                    className="px-6 font-medium"
                  >
                    {isConverting ? 'Generating...' : 'Generate Audio'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Floating Audio Player */}
        {audioUrl && (
          <div className="floating-audio-player">
            <audio
              controls
              className="w-full custom-audio-player"
              src={audioUrl}
            >
              Your browser does not support the audio element.
            </audio>
          </div>
        )}
      </div>
    </div>
  );
};

export default Playground;
